import { Stack } from "@mui/material";
import { isEmpty } from "lodash-es";
import type { FC } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { usePermissions } from "../../../server/auth/use-permissions";
import { useFlag } from "../../../server/optimizely";
import { isEmailChannel } from "../../channels/types";
import { useAccountLimitsContext } from "../../context/account-limit-context";
import { canHaveFollowUp } from "../../follow-up/utils";
import { ConditionalTooltip } from "../../generic/components/conditional-tooltip";
import { HeaderBar } from "../../generic/components/header-bar";
import { HeaderBarTitle } from "../../generic/components/header-bar-title";
import { MomentInsights } from "../../insights/components/moment-insights";
import { useFormSaveState } from "../hooks/use-form-save-state";
import type { ProgramsState } from "../hooks/use-programs";
import type { MomentFormMoment } from "../types/moment-form-moment";
import { MomentLogV2 } from "./audit/moment-log-v2";
import { ArchiveButton } from "./buttons/archive-button";
import { CopyMomentButton } from "./buttons/copy-moment-button";
import { MomentFormCreateFollowUpButton } from "./buttons/moment-form-create-follow-up-button/moment-form-create-follow-up-button";
import { PauseButton } from "./buttons/pause-button";
import { MomentFormDebug } from "./moment-form-debug";
import { useRoundaboutContext } from "./roundabout/roundabout-context";
import { SharingLinkToolbarButton } from "./sharing-link/sharing-link-toolbar-button";

export const FullWidthMomentFormHeader: FC<{
  moment: MomentFormMoment & { isNew: boolean };
  reloadMoment: () => void;
  isNewsletter?: boolean;
  programs: ProgramsState;
}> = ({ moment, reloadMoment, programs }) => {
  const { control, getValues } = useFormContext<MomentFormMoment>();

  const { userReadOnlyRole } = useRoundaboutContext();
  const formSaveState = useFormSaveState(userReadOnlyRole);

  const { super_admin } = usePermissions();
  const { communication_insights } = useAccountLimitsContext();
  const [communicationInsightsFlag] = useFlag("cha-4300-communication-insights");
  const [reviewEnabled] = useFlag("cha-4075-review-moments");

  useWatch({
    control,
    name: [
      "id",
      "channel",
      "notify_channel",
      "only_additional_recipients",
      "target_channel",
      "program_id",
      "title",
      "status",
      "review_status",
    ],
  });
  const [
    momentId,
    channel,
    notify_channel,
    only_additional_recipients,
    target_channel,
    program_id,
    title,
    status,
    review_status,
  ] = getValues([
    "id",
    "channel",
    "notify_channel",
    "only_additional_recipients",
    "target_channel",
    "program_id",
    "title",
    "status",
    "review_status",
  ]);

  const isRecurring =
    moment.schedule?.event?.calendar === true && moment.schedule?.event?.allow_repeats === true;

  const insightsAvailable =
    super_admin && communication_insights && communicationInsightsFlag && status !== "draft";

  return (
    <>
      <HeaderBar navBurger>
        <HeaderBarTitle
          title={{
            type: "fixed",
            value: moment.isNew && isEmpty(title) ? "New Communication" : title,
          }}
          subtitle={programs.data?.viewable?.find(({ id }) => id === program_id)?.title}
          channel={channel}
          status={status}
          reviewStatus={reviewEnabled && review_status ? review_status : undefined}
          disabled={false}
        />
        <Stack direction="row">
          {insightsAvailable && <MomentInsights momentId={momentId} />}
          {isEmailChannel(channel) && (
            <ConditionalTooltip
              placement="bottom"
              arrow
              situations={[
                {
                  title: "You need to publish this Communication before generating a sharing link",
                  condition: moment.status === "draft",
                },
              ]}
            >
              <span>
                <SharingLinkToolbarButton />
              </span>
            </ConditionalTooltip>
          )}
          {!moment.isNew &&
            canHaveFollowUp({
              channel,
              notify_channel,
              only_additional_recipients,
              target_channel,
            }) && (
              <MomentFormCreateFollowUpButton
                saving={formSaveState.saving}
                momentId={momentId}
                isRecurring={isRecurring}
              />
            )}
          {!formSaveState.formDisabled["pause-edit-button"] && (
            <PauseButton
              saving={formSaveState.saving}
              formDisabled={formSaveState.formDisabled["pause-edit-button"]}
              onPaused={reloadMoment}
            />
          )}
          <CopyMomentButton formSaveState={formSaveState} />
          <MomentLogV2 moment={moment} showText={false} />
          <ArchiveButton reloadMoment={reloadMoment} />
          <MomentFormDebug />
        </Stack>
      </HeaderBar>
    </>
  );
};
